import React, { useState, useEffect, useRef } from 'react';
import { Form, Row, Col, Pagination } from 'react-bootstrap';
import { get } from 'aws-amplify/api';
import { useNavigate } from 'react-router-dom';
import StartupCard from '../components/StartupCard'
import 'bootstrap/dist/css/bootstrap.min.css';
import { convertKeysToCamelCase } from '../untils/stringUtils';
import SampleStartupCard, { startupStageLabel } from '../components/SampleStartupCard';
import { STARTUP_VERTICALS, STARTUP_STAGES, RAISE_RANGES, COUNTRIES } from '../untils/startupOptions';

const emptyCriteria = { vertical: '', startupStage: '', country: '', raiseRange: '' };

// Does a startup match every selected filter? Samples can carry several verticals ("a|b|c").
const matchesCriteria = (item, criteria) =>
    (!criteria.vertical || item.vertical === criteria.vertical || (item.verticalTags || '').split('|').includes(criteria.vertical)) &&
    (!criteria.startupStage || item.startupStage === criteria.startupStage) &&
    (!criteria.country || (item.cityCountryHeadquarters || '').split(', ').pop().toLowerCase() === criteria.country.toLowerCase()) &&
    (!criteria.raiseRange || item.raiseRange === criteria.raiseRange);

// Show real and sample startups in a mixed order
const shuffle = (list) => {
    const a = [...list];
    for (let i = a.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [a[i], a[j]] = [a[j], a[i]];
    }
    return a;
};

function MatchPage() {
    const [items, setItems] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(6);
    const [filteredItems, setFilteredItems] = useState([]);
    const [searchCriteria, setSearchCriteria] = useState(emptyCriteria);

    useEffect(() => {
        async function fetchData() {
            try {
                const restOperation = get({
                    apiName: 'fundiiAPI',
                    path: "/services/startups"
                });
                const { body } = await restOperation.response;
                const response = await body.json();
                const camelCaseData = convertKeysToCamelCase(response.message);
                // console.log(camelCaseData);
                if (camelCaseData && camelCaseData.length > 0) {
                    const mixed = shuffle(camelCaseData);
                    setItems(mixed);
                    setFilteredItems(mixed);
                } else {
                    setItems([]);
                    setFilteredItems([]);
                }
            } catch (err) {
                console.log(`Error fetching startups: `, err);
            }
        }

        fetchData();
    }, []);

    // Handle criteria change
    const handleCriteriaChange = (e) => {
        const { name, value } = e.target;
        setSearchCriteria(prevCriteria => ({
            ...prevCriteria,
            [name]: value
        }));
        // setCurrentPage(1); // Reset to first page when criteria changes
    };

    const handleSearch = () => {
        setFilteredItems(items.filter(item => matchesCriteria(item, searchCriteria)));
        setCurrentPage(1); // Reset to first page on search
    };

    const handleClear = () => {
        setSearchCriteria(emptyCriteria);
        setFilteredItems(items);
        setCurrentPage(1);
    };

    // Handle page change
    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
    };


    // Calculate the number of pages
    const totalPages = Math.ceil(filteredItems.length / itemsPerPage);
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    // console.log(filteredItems)
    const currentItems = filteredItems.slice(indexOfFirstItem, indexOfLastItem);

    return (
        <div className="container" style={{ marginTop: '10%' }}>
            <header>
                <Form>
                    <Row className="mb-3">
                        <Col>
                            <Form.Select name="vertical" value={searchCriteria.vertical} onChange={handleCriteriaChange} aria-label="Industry">
                                <option value="">All industries</option>
                                {STARTUP_VERTICALS.map(v => <option key={v} value={v}>{v}</option>)}
                            </Form.Select>
                        </Col>
                        <Col>
                            <Form.Select name="startupStage" value={searchCriteria.startupStage} onChange={handleCriteriaChange} aria-label="Startup stage">
                                <option value="">All startup stages</option>
                                {STARTUP_STAGES.map(v => <option key={v} value={v}>{startupStageLabel(v)}</option>)}
                            </Form.Select>
                        </Col>
                    </Row>
                    <Row className="mb-3">
                        <Col>
                            <Form.Select name="country" value={searchCriteria.country} onChange={handleCriteriaChange} aria-label="Country">
                                <option value="">All countries</option>
                                {COUNTRIES.map(v => <option key={v} value={v}>{v}</option>)}
                            </Form.Select>
                        </Col>
                        <Col>
                            <Form.Select name="raiseRange" value={searchCriteria.raiseRange} onChange={handleCriteriaChange} aria-label="Fundraising / M&A ask">
                                <option value="">Any fundraising / M&amp;A ask</option>
                                {RAISE_RANGES.map(v => <option key={v} value={v}>{v}</option>)}
                            </Form.Select>
                        </Col>
                    </Row>
                </Form>
                <div style={{ display: 'flex', justifyContent: 'center' }}>
                    <button
                        className="btn orange-button"
                        onClick={handleSearch}
                        style={{ width: '70vw', color: 'white'}}
                    >
                        Search
                    </button>
                    <button className="btn btn-outline-secondary ms-2" onClick={handleClear}>
                        Clear
                    </button>
                </div>
            </header>
            {/* Card display for current items */}
            <div className="container mt-5">
                {currentItems.length > 0 ? (
                    <>
                        <div className="row">
                            {currentItems.map((item, index) => (
                                <div className="col-md-4 mb-4" key={index}>
                                    {item.isSample ? <SampleStartupCard startup={item} /> : <StartupCard startup={item} />}
                                </div>
                            ))}
                        </div>

                        {/* Pagination controls */}
                        <Pagination className="justify-content-center">
                            <Pagination.First onClick={() => handlePageChange(1)} disabled={currentPage === 1} />
                            <Pagination.Prev onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} />
                            {[...Array(totalPages).keys()].map(number => (
                                <Pagination.Item key={number + 1} active={number + 1 === currentPage} onClick={() => handlePageChange(number + 1)}>
                                    {number + 1}
                                </Pagination.Item>
                            ))}
                            <Pagination.Next onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages} />
                            <Pagination.Last onClick={() => handlePageChange(totalPages)} disabled={currentPage === totalPages} />
                        </Pagination>
                    </>) : (
                    <div style={{ textAlign: 'center' }}>No items to display</div>
                )}
            </div>
        </div>
    );
}

export default MatchPage;
